import { SLICE_TAG } from '@/constants';

import { apiSlice } from '../api-slice';
import {
    CartStocksSchema,
    InventoryProductListForSale,
    InventoryProductListForStock,
    LowStockListResponseSchema,
    ProductStockDetailsResponseSchema,
    StockAdjustmentListResponseSchema,
    StocksResponseSchema,
} from './inventory.schema';

export const inventoryApi = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        getInventoryProductForSale: builder.query<
            InventoryProductListForSale,
            {
                limit: number;
                page: number;
                search: string;
            }
        >({
            query: (params) => {
                return {
                    url: 'inventory?batch=true',
                    method: 'GET',
                    params,
                };
            },
            providesTags: [SLICE_TAG.INVENTORY_SALE],
            keepUnusedDataFor: 600,
            transformResponse: (response) => {
                return InventoryProductListForSale.parse(response);
            },
        }),

        getInventoryProductForStock: builder.query<
            InventoryProductListForStock,
            object
        >({
            query: (params) => {
                return {
                    url: 'inventory?batch=false',
                    method: 'GET',
                    params,
                };
            },
            providesTags: [SLICE_TAG.INVENTORY_STOCK],
            keepUnusedDataFor: 600,
            transformResponse: (response) => {
                return InventoryProductListForStock.parse(response);
            },
        }),

        getLowStock: builder.query({
            query: (params) => {
                return {
                    url: 'inventory/low-stock',
                    method: 'GET',
                    params,
                };
            },
            providesTags: [SLICE_TAG.INVENTORY_LOW_STOCK],
            keepUnusedDataFor: 600,
            transformResponse(response) {
                return LowStockListResponseSchema.parse(response);
            },
        }),

        getExpiredStocks: builder.query({
            query: (params) => {
                return {
                    url: 'inventory/expired',
                    method: 'GET',
                    params,
                };
            },
            providesTags: [SLICE_TAG.INVENTORY_EXPIRED],
        }),

        getStockOut: builder.query({
            query: (params) => {
                return {
                    url: 'inventory/stock-out',
                    method: 'GET',
                    params,
                };
            },
            providesTags: [SLICE_TAG.INVENTORY_STOCK_OUT],
        }),

        getExpireItems: builder.query({
            query: () => 'inventory/expired',
            providesTags: [SLICE_TAG.INVENTORY_EXPIRED],
            keepUnusedDataFor: 600,
        }),

        getBatchesByProductVariationId: builder.query({
            query: (productVariationId: string) => {
                return {
                    url: `inventory/batches/${productVariationId}`,
                    method: 'GET',
                };
            },
            providesTags: (_result, _error, arg: string) => [
                { type: SLICE_TAG.INVENTORY_BATCHES, id: arg },
            ],
        }),

        adjustStockByBatches: builder.mutation({
            query: (body: any) => {
                return {
                    url: `inventory/adjust-stock-by-batches`,
                    method: 'PATCH',
                    body,
                };
            },
            invalidatesTags: [
                SLICE_TAG.INVENTORY_SALE,
                SLICE_TAG.INVENTORY_STOCK,
            ],
        }),

        getStockAdjustments: builder.query({
            query: (params) => {
                return {
                    url: '/inventory/adjustments',
                    method: 'GET',
                    params,
                };
            },
            providesTags: [SLICE_TAG.INVENTORY_ADJUSTMENTS],
            transformResponse(response) {
                return StockAdjustmentListResponseSchema.parse(response);
            },
        }),

        getStocks: builder.query({
            query: (params) => {
                return {
                    url: '/inventory/stocks',
                    method: 'GET',
                    params,
                };
            },
            providesTags: [SLICE_TAG.INVENTORY_STOCKS],
            transformResponse(response) {
                return StocksResponseSchema.parse(response);
            },
        }),

        getProductStockDetailsByVariationId: builder.query({
            query: (productVariationId: string) => {
                return {
                    url: `inventory/stocks/products/${productVariationId}`,
                    method: 'GET',
                };
            },
            providesTags: (_result, _error, arg: string) => [
                { type: SLICE_TAG.INVENTORY_STOCK, id: arg },
            ],
            transformResponse(response) {
                return ProductStockDetailsResponseSchema.parse(response);
            },
        }),

        getCartStocks: builder.query({
            query: (params) => {
                return {
                    url: '/inventory/stocks/cart',
                    method: 'GET',
                    params,
                };
            },
            providesTags: [SLICE_TAG.INVENTORY_SALE],
            keepUnusedDataFor: 600,
            transformResponse: (response) => {
                return CartStocksSchema.parse(response);
            },
        }),
    }),
});

export const {
    useGetInventoryProductForSaleQuery,
    useGetInventoryProductForStockQuery,
    useGetExpireItemsQuery,
    useGetStockOutQuery,
    useGetLowStockQuery,
    useGetExpiredStocksQuery,
    useLazyGetExpiredStocksQuery,
    useGetBatchesByProductVariationIdQuery,
    useLazyGetBatchesByProductVariationIdQuery,
    useLazyGetExpireItemsQuery,
    useLazyGetInventoryProductForSaleQuery,
    useLazyGetInventoryProductForStockQuery,
    useLazyGetLowStockQuery,
    useLazyGetStockOutQuery,

    useGetStocksQuery,

    useAdjustStockByBatchesMutation,
    useGetStockAdjustmentsQuery,
    useLazyGetStockAdjustmentsQuery,
    useLazyGetStocksQuery,

    useGetProductStockDetailsByVariationIdQuery,
    useLazyGetProductStockDetailsByVariationIdQuery,

    useGetCartStocksQuery,
    useLazyGetCartStocksQuery,
} = inventoryApi;
